import React, { useState, useEffect, useRef } from 'react';
import SVGInline from 'components/SVGInline/SVGInline';
import playerIcons from 'assets/images/player';
import icons from 'style';
import { JumpButtonLabel } from 'components/PlayerUI/PlayerUI.css';
import { OverlayContainer, IconWrapper, JumpIconWrapper, PlaybackIconWrapper } from './KeyboardShortcutOverlay.css';

const PLAYBACK_VISIBLE_DURATION = 1000;
const PLAYBACK_FADE_IN_DURATION = 150;
const PLAYBACK_FADE_OUT_DURATION = 500;
const DEFAULT_VISIBLE_DURATION = 200;
const DEFAULT_FADE_DURATION = 200;

interface KeyboardShortcutOverlayProps {
    iconName: string;
    overlayTrigger: number;
    isEnabled: boolean;
    forwardJumpValue: number;
    backwardJumpValue: number;
}

const KeyboardShortcutOverlay: React.FC<KeyboardShortcutOverlayProps> = ({
    iconName,
    overlayTrigger,
    isEnabled,
    forwardJumpValue,
    backwardJumpValue,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const handledTriggerRef = useRef(overlayTrigger);

    const isPlaybackIcon = iconName === 'play' || iconName === 'pause';
    const isGoToLiveIcon = iconName === 'goToLive';
    const isJumpIcon = iconName === 'jumpFw' || iconName === 'jumpBw';
    const usesPlaybackTiming = isPlaybackIcon || isGoToLiveIcon || isJumpIcon;
    const visibleDuration = usesPlaybackTiming ? PLAYBACK_VISIBLE_DURATION : DEFAULT_VISIBLE_DURATION;
    const fadeInDuration = usesPlaybackTiming ? PLAYBACK_FADE_IN_DURATION : DEFAULT_FADE_DURATION;
    const fadeOutDuration = usesPlaybackTiming ? PLAYBACK_FADE_OUT_DURATION : DEFAULT_FADE_DURATION;

    useEffect(() => {
        const isNewTrigger = overlayTrigger !== handledTriggerRef.current;
        handledTriggerRef.current = overlayTrigger;

        if (!isEnabled) {
            setIsVisible(false);
            return () => {};
        }

        if (isNewTrigger && overlayTrigger) {
            setIsVisible(true);
            const timer = setTimeout(() => setIsVisible(false), visibleDuration);
            return () => clearTimeout(timer);
        }
        return () => {};
    }, [overlayTrigger, isEnabled, visibleDuration]);

    const getIcon = (name: string) => {
        const iconKey = `${name}Icon` as keyof typeof playerIcons;
        return playerIcons[iconKey] || playerIcons.playIcon;
    };

    const getPosition = (): 'left' | 'right' | 'center' => {
        switch (iconName) {
            case 'jumpFw':
                return 'right';
            case 'jumpBw':
                return 'left';
            default:
                return 'center';
        }
    };

    const renderContent = () => {
        if (isPlaybackIcon) {
            return (
                <PlaybackIconWrapper>
                    <SVGInline svg={iconName === 'pause' ? icons.playIconSimple : icons.pauseIconSimple} />
                </PlaybackIconWrapper>
            );
        }

        if (isGoToLiveIcon) {
            return (
                <PlaybackIconWrapper>
                    <SVGInline svg={icons.icoGoToLiveProgram} />
                </PlaybackIconWrapper>
            );
        }

        if (isJumpIcon) {
            const isForward = iconName === 'jumpFw';
            const jumpValue = isForward ? forwardJumpValue : backwardJumpValue;
            return (
                <PlaybackIconWrapper>
                    <JumpIconWrapper>
                        <SVGInline svg={isForward ? icons.iconJumpForwardStrong : icons.iconJumpBackwardStrong} />
                        <JumpButtonLabel>{jumpValue}</JumpButtonLabel>
                    </JumpIconWrapper>
                </PlaybackIconWrapper>
            );
        }

        return (
            <IconWrapper>
                <img src={getIcon(iconName)} alt="" />
            </IconWrapper>
        );
    };

    return (
        <OverlayContainer visible={isVisible} position={getPosition()} fadeInDuration={fadeInDuration} fadeOutDuration={fadeOutDuration}>
            {renderContent()}
        </OverlayContainer>
    );
};

export default KeyboardShortcutOverlay;
